import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { specials } from '@/mocks/specials';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const solid = scrolled || !isHome || menuOpen || searchOpen;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setSearchOpen(false);
  };

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header
      className={`fixed top-0 left-0 z-50 w-full transition-all duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-gradient-to-b from-background-50/80 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-1 cursor-pointer shrink-0">
          <span className="font-heading text-2xl font-extrabold text-primary-500 leading-none">91</span>
          <span className="font-heading text-2xl font-extrabold text-foreground-950 leading-none">.con</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              isActive('/') ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            首页
          </Link>

          <div className="relative group">
            <Link
              to="/category/film"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-0.5 ${
                location.pathname.startsWith('/category') ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              资讯
              <i className="ri-arrow-down-s-line text-base" />
            </Link>
            <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
              <div className="w-52 rounded-lg bg-background-100 border border-background-200 p-2">
                {categories.map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    className="block px-3 py-2 rounded-md text-sm text-foreground-700 hover:text-primary-400 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    {c.name}
                  </Link>
                ))}
                <div className="h-px bg-background-200 my-1" />
                <Link
                  to="/videos"
                  className="block px-3 py-2 rounded-md text-sm text-foreground-700 hover:text-primary-400 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  全部视频
                </Link>
              </div>
            </div>
          </div>

          <Link
            to="/videos"
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              isActive('/videos') ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            视频
          </Link>

          <div className="relative group">
            <Link
              to={`/special/${specials[0].id}`}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-0.5 ${
                location.pathname.startsWith('/special') ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              专题
              <i className="ri-arrow-down-s-line text-base" />
            </Link>
            <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
              <div className="w-60 rounded-lg bg-background-100 border border-background-200 p-2">
                {specials.map((s) => (
                  <Link
                    key={s.id}
                    to={`/special/${s.id}`}
                    className="block px-3 py-2 rounded-md text-sm text-foreground-700 hover:text-primary-400 hover:bg-background-200/70 transition-colors cursor-pointer truncate"
                  >
                    {s.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link
            to="/about"
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              isActive('/about') ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            关于
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            aria-label="搜索"
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:text-primary-400 hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className="ri-search-line text-lg" />
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="菜单"
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:text-primary-400 hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="w-full px-4 md:px-8 pb-4 animate-soft-fade">
          <form onSubmit={submitSearch} className="flex items-center gap-2 w-full max-w-3xl">
            <div className="flex items-center gap-2 flex-1 bg-background-100 border border-background-200 rounded-md px-3 h-11 focus-within:border-primary-500 transition-colors">
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索娱乐资讯、视频或标签"
                className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="md:hidden w-full bg-background-50 border-t border-background-200 max-h-[calc(100vh-4rem)] overflow-y-auto animate-soft-fade">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link to="/" className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">首页</Link>
            <Link to="/videos" className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">视频</Link>
            <Link to="/about" className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">关于我们</Link>

            <p className="px-3 pt-3 pb-1 text-xs text-foreground-500">资讯分类</p>
            <div className="flex flex-wrap gap-2 px-3">
              {categories.map((c) => (
                <Link
                  key={c.key}
                  to={`/category/${c.key}`}
                  className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 cursor-pointer"
                >
                  {c.name}
                </Link>
              ))}
            </div>

            <p className="px-3 pt-4 pb-1 text-xs text-foreground-500">热门专题</p>
            <div className="flex flex-col">
              {specials.map((s) => (
                <Link
                  key={s.id}
                  to={`/special/${s.id}`}
                  className="px-3 py-3 rounded-md text-sm text-foreground-700 hover:bg-background-100 cursor-pointer truncate"
                >
                  {s.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}